import React, {
  createContext,
  type ReactNode,
  useContext,
  useState,
} from "react";
import { type DeviceScreen, type UIElementBounds } from "./models";
import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { appetizeClientAtom, appetizeSessionStore } from "@/atoms";
import { type Dimensions, type SettingsData } from "@/utils/types";
import { sessionMutationAtom } from "@/utils/simulator";
import { type DeviceContextState } from "@/routes/test-builder-page";
import { getNonOccludedElements } from "@/utils/helpers";
import { useStore } from "@tanstack/react-store";

export interface ElementHint {
  text: string;
  isResourceId: boolean;
}

interface DeviceContextType {
  isLoading: boolean;
  hoveredElement: TreeNode | null;
  setHoveredElement: (element: TreeNode | null) => void;
  inspectedElement: TreeNode | null;
  setInspectedElement: (id: TreeNode | null) => void;
  deviceScreen: DeviceScreen | undefined;
  footerHint: ElementHint | null;
  setFooterHint: (footer: ElementHint | null) => void;
  currentCommandValue: string;
  setCurrentCommandValue: (id: string) => void;
}

interface TreeNode {
  attributes?: Record<string, string | boolean>;
  children?: TreeNode[];
  clickable?: boolean | null;
  enabled?: boolean | null;
  focused?: boolean | null;
  checked?: boolean | null;
  selected?: boolean | null;
  bounds?: UIElementBounds;
  drawingOrderPath?: string[];
  indexPath: string[];
  drawingOrder?: number;
  isKeyboardElement?: boolean;
  isKeyboardParent?: boolean;
  isFixed: boolean;
}

interface DeviceProviderProps {
  children: ReactNode;
  defaultInspectedElement?: UIElement;
  settingsData: SettingsData;
  deviceContextState: DeviceContextState;
}

/**
 * Detects if the UI data is from an iOS device
 */
const isIOSPlatform = (
  platformInfo: string | undefined,
  uiData: any
): boolean => {
  // If platform info is explicitly provided, use it
  if (platformInfo) {
    return platformInfo.toLowerCase() === "ios";
  }

  // Otherwise try to detect from UI data
  if (Array.isArray(uiData) && uiData[0]?.type === "app") {
    // Check for iOS-specific attributes in the first few nodes
    const checkDepth = 10; // Only check a reasonable depth
    let depth = 0;
    let nodesToCheck = [uiData[0]];

    while (nodesToCheck.length > 0 && depth < checkDepth) {
      const nextLevel: any[] = [];

      for (const node of nodesToCheck) {
        // Look for iOS-specific class attributes
        if (
          node.attributes?.class &&
          (node.attributes.class.startsWith("UI") ||
            node.attributes.baseClass?.startsWith("UI"))
        ) {
          return true;
        }

        // Add children for next iteration
        if (node.children && Array.isArray(node.children)) {
          nextLevel.push(...node.children);
        }
      }

      nodesToCheck = nextLevel;
      depth++;
    }
  } else if (typeof uiData === "string") {
    // For XML strings, look for iOS-specific node types
    return (
      uiData.includes("XCUIElementType") || uiData.includes("UIAApplication")
    );
  }

  // Default to Android if we can't determine
  return false;
};

const getVisibleItems = ({
  elements,
  deviceDimensions,
  allowNonClickableItems = false,
  allowFullScreenItems = false,
  shouldIncludeKeyboardParentElement = false,
  ignoredElements,
}: {
  elements: TreeNode[];
  deviceDimensions?: Dimensions;
  allowNonClickableItems?: boolean;
  allowFullScreenItems?: boolean;
  shouldIncludeKeyboardParentElement?: boolean;
  ignoredElements: string[];
}): TreeNode[] => {
  const deviceWidth = deviceDimensions?.width ?? 0;
  const deviceHeight = deviceDimensions?.height ?? 0;

  return elements
    .filter((element) => {
      const isClickable = element.clickable ?? false;
      const isVisible = element?.attributes?.visible !== false;
      const text =
        element?.attributes?.text !== ""
          ? element?.attributes?.text
          : undefined;
      const accessibilityText =
        element?.attributes?.accessibilityText ??
        element?.attributes?.["content-desc"] ??
        element?.attributes?.accessibilityLabel;
      const resourceId = (element?.attributes?.["resource-id"] ??
        element?.attributes?.accessibilityIdentifier) as string;
      if (ignoredElements.includes(resourceId)) {
        return false;
      }

      const hasValidProperties = [text, accessibilityText, resourceId].some(
        (prop) => prop != null && prop.trim()?.length > 0
      );

      const xStart = element.bounds?.x ?? 0;
      const elementWidth = element.bounds?.width ?? 0;
      const xEnd = xStart + elementWidth;

      const visibleLeft = Math.max(0, xStart);
      const visibleRight = Math.min(deviceWidth, xEnd);
      const visibleWidth = Math.max(0, visibleRight - visibleLeft);

      const yStart = element.bounds?.y ?? 0;
      const elementHeight = element.bounds?.height ?? 0;
      const yEnd = yStart + elementHeight;

      const visibleTop = Math.max(0, yStart);
      const visibleBottom = Math.min(deviceHeight, yEnd);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      const isOnScreen = visibleWidth > 0 && visibleHeight > 0;

      const isFullScreen =
        element.bounds?.height === deviceDimensions?.height &&
        element.bounds?.width === deviceDimensions?.width;

      return (
        (allowFullScreenItems || !isFullScreen) &&
        (allowNonClickableItems || isClickable) &&
        isVisible &&
        (hasValidProperties ||
          (shouldIncludeKeyboardParentElement && element.isKeyboardParent)) &&
        isOnScreen
      );
    })
    .map((element) => {
      // Calculate visible bounds
      const xStart = element.bounds?.x ?? 0;
      const elementWidth = element.bounds?.width ?? 0;
      const xEnd = xStart + elementWidth;

      const visibleLeft = Math.max(0, xStart);
      const visibleRight = Math.min(deviceWidth, xEnd);
      const visibleWidth = Math.max(0, visibleRight - visibleLeft);

      const yStart = element.bounds?.y ?? 0;
      const elementHeight = element.bounds?.height ?? 0;
      const yEnd = yStart + elementHeight;

      const visibleTop = Math.max(0, yStart);
      const visibleBottom = Math.min(deviceHeight, yEnd);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      // Return element with updated bounds
      return {
        ...element,
        bounds: {
          x: visibleLeft,
          y: visibleTop,
          width: visibleWidth,
          height: visibleHeight,
        },
      };
    });
};

const filterNonEssentialUIData = (uiElements: TreeNode[]) => {
  // Leave only bounds, text, resourceId, contentDesc, accessibilityLabels to lower number of tokens send with uiHierarchy data

  try {
    const filteredUiElements = uiElements.map((elem) => {
      const bounds = elem?.bounds;
      const text = (elem?.attributes?.text ?? "") as string;
      const resourceId = (elem?.attributes?.["resource-id"] ?? "") as string;
      const contentDesc = (elem?.attributes?.["content-desc"] ?? "") as string;
      const accessibilityLabel = (elem?.attributes?.accessibilityLabel ??
        "") as string;
      const accessibilityIdentifier = (elem?.attributes
        ?.accessibilityIdentifier ?? "") as string;
      const hint = (elem?.attributes?.hint ?? "") as string;

      // Build the filtered attributes object
      const filteredAttributes: Record<string, any> = {};

      if (text.length > 0) {
        filteredAttributes.text = text;
      }
      if (resourceId.length > 0) {
        filteredAttributes["resource-id"] = resourceId;
      }
      if (contentDesc.length > 0) {
        filteredAttributes["content-desc"] = contentDesc;
      }
      if (accessibilityLabel.length > 0) {
        filteredAttributes.accessibilityLabel = accessibilityLabel;
      }
      if (accessibilityIdentifier.length > 0) {
        filteredAttributes.accessibilityIdentifier = accessibilityIdentifier;
      }
      if (hint.length > 0) {
        filteredAttributes.hint = hint;
      }
      if (elem.isKeyboardElement) {
        filteredAttributes.isKeyboardElement = elem.isKeyboardElement;
      }
      if (elem.isKeyboardParent) {
        filteredAttributes.isKeyboardParent = elem.isKeyboardParent;
      }

      return {
        ...(bounds != null && {
          bounds: {
            x: Math.floor(bounds.x),
            y: Math.floor(bounds.y),
            width: Math.floor(bounds.width),
            height: Math.floor(bounds.height),
          },
        }),
        ...(Object.keys(filteredAttributes).length > 0 && {
          attributes: filteredAttributes,
        }),
      };
    });

    // Additionally filter out elements, which don't have any meaningful description
    return filteredUiElements.filter((elem) => Object.keys(elem).length > 1);
  } catch (e) {
    console.error("filterNonEssentialUIData:", e);
    return uiElements;
  }
};

/**
 * Universal function for transforming UI data (regardless of format)
 */
const transformUIData = (uiData: any, isIOS: boolean = false): TreeNode[] => {
  // For JSON data
  if (Array.isArray(uiData) && uiData[0]?.type === "app") {
    if (isIOS) {
      return transformAppetizeIOSJSON(uiData);
    } else {
      return transformAppetizeAndroidJSON(uiData);
    }
  }

  // For XML data
  if (typeof uiData === "string") {
    if (isIOS) {
      return transformIosUIElements(uiData);
    } else {
      const transformedUi = transformAndroidUIElements(uiData);
      return gatherElements(transformedUi.at(0)!, []);
    }
  }

  // For TreeNode data (already transformed)
  if (uiData && typeof uiData === "object") {
    return gatherElements(uiData, []);
  }

  // Empty result for unsupported formats
  console.error("Unsupported UI data format", uiData);
  return [];
};

/**
 * Gather all elements from a tree into a flat list
 */
const gatherElements = (
  tree: TreeNode,
  list: TreeNode[],
  parent?: TreeNode
): TreeNode[] => {
  if (parent?.clickable && !tree.clickable) {
    tree.clickable = true;
  }
  if (
    (parent?.isFixed ?? false) ||
    (tree.attributes?.type?.includes("NavigationBar") ?? false)
  ) {
    tree.isFixed = true;
  }
  if (tree.children != null) {
    tree.children.forEach((child: any) => {
      gatherElements(child, list, tree);
    });
  }

  const { children, ...rest } = tree;

  list.push(rest);
  return list;
};

/**
 * Transform Appetize JSON format to TreeNode structure
 */
const transformAppetizeAndroidJSON = (appetizeData: any[]): TreeNode[] => {
  // Extract the app node which contains all UI elements
  const appNode = appetizeData[0];
  if (!appNode || appNode.type !== "app") {
    console.error("Invalid Appetize data format: app node not found");
    return [];
  }

  // Process nodes recursively with path building
  const processNode = (
    node: any,
    parentDrawingOrderPath: string[] = [],
    parentIndexPath: string[] = []
  ): TreeNode => {
    // Build path: use existing path or construct from indexes
    let currentIndexPath: string[] = [];
    if (node.path && typeof node.path === "string") {
      // Use existing path if available
      currentIndexPath = node.path.split("/").filter(Boolean);
    } else {
      // Build path from index attributes
      const index = node.attributes?.index || node.index;
      if (index !== undefined && index !== null) {
        currentIndexPath = [...parentIndexPath, index.toString()];
      } else {
        // Fallback: use parent path if no index available
        currentIndexPath = parentIndexPath;
      }
    }

    const bounds = node.bounds || { x: 0, y: 0, width: 0, height: 0 };

    // Extract attributes
    const attributes: Record<string, string | boolean> = {
      ...node.attributes,
    };

    const currentDrawingOrder = node.attributes?.drawingOrder ?? "0";
    const currentDrawingOrderPath = [
      ...parentDrawingOrderPath,
      currentDrawingOrder,
    ];

    // Process children with current path as parent
    const children = node.children
      ? node.children.map((child: any) =>
          processNode(child, currentDrawingOrderPath, currentIndexPath)
        )
      : [];

    // Create TreeNode
    const treeNode: TreeNode = {
      attributes,
      bounds,
      drawingOrderPath: currentDrawingOrderPath,
      drawingOrder: parseInt(currentDrawingOrder),
      indexPath: currentIndexPath,
      clickable: attributes.clickable === "true",
      enabled: attributes.enabled === "true",
      focused: attributes.focused === "true",
      checked:
        attributes.checkable === "true" && attributes.selected === "true",
      selected: attributes.selected === "true",
    };

    if (children.length > 0) {
      treeNode.children = children;
    }

    return treeNode;
  };

  // Process all top-level nodes (usually application and system nodes)
  const allNodes = appNode.children.map((child, index) =>
    processNode(child, [index.toString()])
  );

  // Get all nodes in a flat structure
  let flattenedNodes: TreeNode[] = [];
  allNodes.forEach((node) => {
    flattenedNodes = [...flattenedNodes, ...gatherElements(node, [])];
  });

  return flattenedNodes;
};
/**
 * Transform Appetize iOS JSON format to TreeNode structure
 */
const transformAppetizeIOSJSON = (appetizeData: any[]): TreeNode[] => {
  // Extract the app node which contains all UI elements
  const appNode = appetizeData[0];
  if (!appNode || appNode.type !== "app") {
    console.error("Invalid Appetize data format: app node not found");
    return [];
  }

  // Process nodes recursively
  const processNode = (node: any): TreeNode => {
    // Extract basic properties
    const path = node.path || "";
    const bounds = node.bounds || { x: 0, y: 0, width: 0, height: 0 };

    // Extract attributes
    const attributes: Record<string, string | boolean> = {
      ...node.attributes,
    };

    // iOS-specific attribute processing
    const baseClass = (attributes.baseClass as string) || "";
    const className = (attributes.class as string) || "";

    // Determine if element is interactive
    let isClickable = false;

    // Controls (buttons, text fields, etc.) are almost always interactive
    if (
      baseClass &&
      (baseClass.includes("UIControl") || baseClass.includes("TextField"))
    ) {
      isClickable = true;
    }

    // Any UI element with userInteractionEnabled should be clickable
    if (attributes.userInteractionEnabled === true) {
      isClickable = true;
    }

    // Special case for text fields
    if (
      className &&
      (className.includes("UITextField") ||
        className.includes("UISearchBar") ||
        className.includes("UITextView"))
    ) {
      isClickable = true;
    }

    // Special case for buttons
    if (className && className.includes("Button")) {
      isClickable = true;
    }

    // Images with accessibilityIdentifier are often interactive
    if (
      className &&
      className.includes("UIImageView") &&
      attributes.accessibilityIdentifier
    ) {
      isClickable = true;
    }

    // Process children
    const children = node.children
      ? node.children.map((child: any) => processNode(child))
      : [];

    // Create TreeNode
    const treeNode: TreeNode = {
      attributes,
      bounds,
      indexPath: path.split("/").filter(Boolean),
      clickable: isClickable,
      enabled: attributes.userInteractionEnabled === true,
      focused: attributes.focused === true,
      checked: false, // iOS doesn't use "checked" in the same way
      selected: false, // iOS doesn't use "selected" in the same way
    };

    if (children.length > 0) {
      treeNode.children = children;
    }

    return treeNode;
  };

  // Process all top-level nodes (usually window nodes)
  const allNodes = appNode.children.map((child) => processNode(child));

  // Get all nodes in a flat structure
  let flattenedNodes: TreeNode[] = [];
  allNodes.forEach((node) => {
    flattenedNodes = [...flattenedNodes, ...gatherElements(node, [])];
  });

  return flattenedNodes;
};

/**
 * Transform iOS UI XML to TreeNode structure
 */
const transformIosUIElements = (xmlString: string): TreeNode[] => {
  const cleanXmlString = xmlString.replace(/\\"/g, '"').replace(/\\n/g, "\n");

  // Parse bounds from element attributes
  const parseBounds = (element: Element): UIElementBounds | undefined => {
    try {
      const x = parseFloat(element.getAttribute("x") ?? "0");
      const y = parseFloat(element.getAttribute("y") ?? "0");
      const width = parseFloat(element.getAttribute("width") ?? "0");
      const height = parseFloat(element.getAttribute("height") ?? "0");
      return { x, y, width, height };
    } catch (error) {
      console.error("Error parsing bounds:", error);
      return undefined;
    }
  };

  // Extract attributes based on element type
  const extractAttributes = (element: Element) => {
    const type = element.getAttribute("type") ?? "";
    const name = element.getAttribute("name") ?? "";
    const label = element.getAttribute("label") ?? "";
    const value = element.getAttribute("value") ?? "";

    // iOS Accessibility Standard Mapping:
    // - accessibilityIdentifier -> name (this is the resource-id equivalent)
    // - accessibilityLabel -> label (user-facing description)
    // - accessibilityValue -> value (current value for controls)

    return {
      text: value,
      "resource-id": name, // accessibilityIdentifier
      "content-desc": label, // accessibilityLabel
      hintText: "",
      accessibilityText: label,
      visible: element.getAttribute("visible") === "true",
      index: element.getAttribute("index") ?? "0",
      type,
    };
  };
  // Parse XML document
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(cleanXmlString, "text/xml");

  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError != null) {
    console.error("XML parsing error:", parseError);
    return [];
  }

  const appElement = xmlDoc.querySelector("XCUIElementTypeApplication");
  if (appElement == null) {
    console.error("No application element found in XML");
    return [];
  }

  // Process elements in hierarchical structure
  const buildElementHierarchy = (
    element: Element,
    depth = 0,
    parentPath: string[] = [],
    isKeyboardElement: boolean = false
  ): TreeNode => {
    const bounds = parseBounds(element);
    const attributes = extractAttributes(element);
    const type = element.getAttribute("type") ?? "";
    const name = element.getAttribute("name") ?? "";
    const accessible = element.getAttribute("accessible") === "true";
    const enabled = element.getAttribute("enabled") === "true";
    const index = element.getAttribute("index") ?? "0";

    // Get child elements
    const childElements = Array.from(element.children).filter(
      (child) =>
        child.nodeType === 1 &&
        child.nodeName.startsWith("XCUIElementType") &&
        !["AXFrame"].includes(child.nodeName)
    );

    const isKeyboardParent = attributes.type === "XCUIElementTypeKeyboard";
    // Process children with path information
    const children = childElements.map((child) =>
      buildElementHierarchy(
        child,
        depth + 1,
        [...parentPath, index],
        isKeyboardElement || isKeyboardParent
      )
    );

    // Determine if element is interactive
    const isNamedControl =
      name.length > 0 &&
      (name.includes("button") ||
        type.includes("Button") ||
        type.includes("Control"));

    // Enhanced clickable detection: also check for TextField, SearchField, etc.
    const isInputField =
      type.includes("TextField") ||
      type.includes("SearchField") ||
      type.includes("TextInput");

    const clickable =
      (accessible && enabled) ||
      isNamedControl ||
      type.includes("Button") ||
      isInputField;

    const treeNode: TreeNode = {
      attributes,
      clickable,
      enabled: element.getAttribute("enabled") === "true",
      focused: element.getAttribute("focused") === "true",
      checked: element.getAttribute("checked") === "true",
      selected: element.getAttribute("selected") === "true",
      bounds,
      drawingOrderPath: [...parentPath, index],
      isKeyboardElement,
      isKeyboardParent,
    };

    if (children.length > 0) {
      treeNode.children = children;
    }

    return treeNode;
  };

  // Build element hierarchy and flatten it
  return gatherElements(buildElementHierarchy(appElement), []);
};

/**
 * Transform Android UI XML to TreeNode structure
 */
const transformAndroidUIElements = (xmlString: string): TreeNode[] => {
  // Clean the XML string - remove escaped quotes and newlines
  const cleanXmlString = xmlString.replace(/\\"/g, '"').replace(/\\n/g, "\n");

  // Parse bounds from Android-style bounds string "[x,y][x2,y2]"
  const parseBounds = (boundsStr: string): UIElementBounds | undefined => {
    try {
      const matches = boundsStr.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
      if (matches == null) return undefined;

      const [_, x1, y1, x2, y2] = matches.map(Number);
      return {
        x: x1,
        y: y1,
        width: x2 - x1,
        height: y2 - y1,
      };
    } catch (error) {
      console.error("Error parsing bounds:", error);
      return undefined;
    }
  };

  // Extract attributes from Android element
  const extractAttributes = (element: Element) => ({
    text: element.getAttribute("text") ?? "",
    "resource-id": element.getAttribute("resource-id") ?? "",
    "content-desc": element.getAttribute("content-desc") ?? "",
    hintText: "", // Android doesn't have a direct equivalent
    hint: element.getAttribute("hint") ?? "",
    accessibilityText: element.getAttribute("content-desc") ?? "",
    visible: element.getAttribute("displayed") === "true",
    index: element.getAttribute("index") ?? "0",
    class: element.getAttribute("class") ?? "",
    package: element.getAttribute("package") ?? "",
  });

  // Convert an Element to our TreeNode format with children
  const elementToTreeNode = (
    element: Element,
    parentPath: string[] = [],
    parentPathIndex: string[] = [],
    siblingIndex: number = 0 // Track position among siblings
  ): TreeNode => {
    const boundsStr = element.getAttribute("bounds");
    const bounds = boundsStr ? parseBounds(boundsStr) : undefined;

    if (!bounds) {
      console.warn(
        `Element with missing/invalid bounds: ${
          element.getAttribute("class") ?? "unknown"
        } with resource-id: ${element.getAttribute("resource-id") ?? "none"}`
      );
    }

    const attributes = extractAttributes(element);
    const baseDrawingOrder = element.getAttribute("drawing-order") ?? "0";
    const currentIndex = element.getAttribute("index") ?? "0";
    const currentIndexPath = [...parentPathIndex, currentIndex];

    // Calculate effective drawing order considering sibling position
    // When base drawing orders are the same, use sibling index as tiebreaker
    // Multiply by 1000 to leave room for the sibling index
    const effectiveDrawingOrder =
      parseInt(baseDrawingOrder) * 1000 + siblingIndex;

    // Use the effective order in the path (as string)
    const currentDrawingOrderPath = [
      ...parentPath,
      effectiveDrawingOrder.toString(),
    ];

    // Process children with their sibling indices
    const children = Array.from(element.children)
      .filter((child) => child.nodeType === 1)
      .map((child, index) =>
        elementToTreeNode(
          child,
          currentDrawingOrderPath,
          currentIndexPath,
          index // Pass sibling index
        )
      );

    const node: TreeNode = {
      attributes,
      clickable: element.getAttribute("clickable") === "true",
      enabled: element.getAttribute("enabled") === "true",
      focused: element.getAttribute("focused") === "true",
      checked: element.getAttribute("checked") === "true",
      selected: element.getAttribute("selected") === "true",
      bounds,
      drawingOrderPath: currentDrawingOrderPath,
      indexPath: currentIndexPath,
      drawingOrder: effectiveDrawingOrder, // Use effective order
    };

    if (children.length > 0) {
      node.children = children;
    }

    return node;
  };

  // Parse XML string into DOM
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(cleanXmlString, "text/xml");

  // Check for parsing errors
  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError != null) {
    console.error("XML parsing error:", parseError);
    return [];
  }

  // Find root element (hierarchy)
  const hierarchyElement = xmlDoc.querySelector("hierarchy");
  if (hierarchyElement == null) {
    console.error("No hierarchy element found in XML");
    return [];
  }

  const rootNode = elementToTreeNode(hierarchyElement);
  return [rootNode];
};

/**
 * Process elements into DeviceScreen format
 */
const processElements = (
  elements: TreeNode[],
  deviceData: any
): DeviceScreen => {
  const uiElements = elements.map<UIElement>((element) => {
    const bounds = element.bounds;
    const text =
      element?.attributes?.text !== "" ? element?.attributes?.text : undefined;
    const drawingOrderPath = element.drawingOrderPath;
    const indexPath = element.indexPath;
    const drawingOrder =
      element.drawingOrder ?? element.attributes?.["drawing-order"];
    const hint = element?.attributes?.hintText ?? "";
    const hintText = element?.attributes?.hintText;
    const accessibilityText =
      element?.attributes?.accessibilityText ??
      element?.attributes?.["content-desc"] ??
      element?.attributes?.accessibilityLabel;
    const resourceId =
      element?.attributes?.["resource-id"] ??
      element?.attributes?.accessibilityIdentifier;

    const id = [
      drawingOrderPath ?? indexPath,
      resourceId,
      accessibilityText,
      text,
      hintText,
      hint,
    ]
      .filter(Boolean)
      .join("-");

    return {
      id,
      bounds,
      text,
      hint,
      hintText,
      accessibilityText,
      resourceId,
      drawingOrderPath,
      drawingOrder,
      indexPath,
      clickable: element.clickable,
    };
  });

  return {
    width: deviceData.device.screen.width,
    height: deviceData.device.screen.height,
    devicePixelRatio: deviceData.device.screen.devicePixelRatio,
    offset: deviceData.device.embed.screen.offset,
    elements: uiElements,
  };
};

// Context Provider
const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const DeviceProvider = ({
  children,
  defaultInspectedElement,
  settingsData,
  deviceContextState,
}: DeviceProviderProps) => {
  const session = useStore(appetizeSessionStore, (state) => state);
  const gptDriverSimulator = useAtomValue(sessionMutationAtom);
  const client = useAtomValue(appetizeClientAtom);

  const {
    data,
    isLoading,
    error: fetchError,
  } = useQuery<DeviceScreen>({
    queryKey: ["deviceScreen"],
    enabled:
      ((client != null && settingsData.useAppetizeCommands) ||
        gptDriverSimulator.data != null) &&
      (deviceContextState.uiInspectorEnabled ||
        deviceContextState.recordInteractionsEnabled),
    queryFn: async () => {
      try {
        if (session?.getData() != null && settingsData.useAppetizeCommands) {
          // Appetize session case
          const unparsedUi = await session.getData()!.getUI();
          console.log("unparsedUi", unparsedUi);

          // Detect platform
          const platform =
            session.getData().device.platform?.toLowerCase() || "";
          const isIOS = isIOSPlatform(platform, unparsedUi);
          console.log(`Detected platform: ${isIOS ? "iOS" : "Android"}`);

          // Transform UI data based on platform
          const elements = transformUIData(unparsedUi, isIOS);
          console.log("transformed elements", elements);

          const visibleItems = getVisibleItems({
            elements,
            deviceDimensions: session.getData().device.screen,
            allowNonClickableItems: true,
            ignoredElements: settingsData.uiInspector.ignoredElements,
          });
          const nonOccludedItems = getNonOccludedElements({
            items: visibleItems,
          });
          console.log("visible elements", nonOccludedItems);

          return processElements(nonOccludedItems, session.getData());
        } else if (gptDriverSimulator?.data != null) {
          // GPT driver simulator case
          const unparsedUI = await gptDriverSimulator.data.getUI();
          // console.log("unparsedUI", unparsedUI);

          // Detect platform
          const platform =
            gptDriverSimulator.data.device.platform?.toLowerCase() || "";
          const isIOS = isIOSPlatform(platform, unparsedUI);
          // console.log(`Detected platform: ${isIOS ? "iOS" : "Android"}`);

          // Transform UI data based on platform
          const elements = transformUIData(unparsedUI, isIOS);
          console.log("transformed elements", elements);

          // Prioritize elements based on platform
          const visibleItems = getVisibleItems({
            elements,
            deviceDimensions: gptDriverSimulator.data.device.screen,
            allowNonClickableItems: true,
            ignoredElements: settingsData.uiInspector.ignoredElements,
          });
          const nonOccludedItems = getNonOccludedElements({
            items: visibleItems,
          });
          console.log("visible elements", nonOccludedItems);

          return processElements(nonOccludedItems, {
            device: {
              screen: {
                ...gptDriverSimulator.data.device.screen,
                devicePixelRatio: 1,
              },
              embed: {
                screen: {
                  offset: { x: 0, y: 0 },
                },
              },
            },
          });
        } else {
          // Default case
          return {
            width: client.device.screen.width,
            height: client.device.screen.height,
            elements: [],
          };
        }
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    refetchInterval: 1000,
  });

  const [hoveredElement, setHoveredElement] = useState<TreeNode | null>(null);
  const [inspectedElement, setInspectedElement] = useState<TreeNode | null>(
    defaultInspectedElement ?? null
  );
  const [footerHint, setFooterHint] = useState<ElementHint | null>(null);
  const [currentCommandValue, setCurrentCommandValue] = useState<string>("");

  return (
    <DeviceContext.Provider
      value={{
        isLoading,
        hoveredElement,
        setHoveredElement,
        inspectedElement,
        setInspectedElement,
        footerHint,
        setFooterHint,
        deviceScreen: data,
        currentCommandValue,
        setCurrentCommandValue,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
};

export const useDeviceContext = () => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error("useDeviceContext must be used within a DeviceProvider");
  }
  return context;
};

export { transformUIData, filterNonEssentialUIData, getVisibleItems };
export type { TreeNode };
